import type { BankRedirectionResponse, OnboardResponse } from '../types';

// Bank handoff helpers. Two distinct mechanisms, by endpoint:
//
//   /apply, /onboard   → hidden-form POST of `merchant` + `data` to
//                        `redirectionUrl`. `data` is a signed/encoded payload
//                        the bank requires in the POST body to authorize the
//                        handoff, so we cannot navigate via a GET.
//   /hf/manage/card    → plain GET redirect to `redirectUrl` (singular). The
//                        card-management surface needs no signed payload.

interface BankFormHandoff {
	merchant: string;
	/** Signed/encoded payload — POSTed as the `data` field. */
	data: string;
	/** Form action — the response's `redirectionUrl`. */
	url: string;
}

function appendHiddenInput(form: HTMLFormElement, name: string, value: string) {
	const input = document.createElement('input');
	input.type = 'hidden';
	input.name = name;
	// Set via property (not innerHTML) so payloads containing quotes/special
	// characters survive intact and can't break out of the markup.
	input.value = value;
	form.appendChild(input);
}

/**
 * Builds a hidden form and POSTs `merchant` + `data` to the bank URL, then
 * submits it (full-page navigation). No-op during SSR.
 */
export function postToBank({ merchant, data, url }: BankFormHandoff): void {
	if (typeof document === 'undefined') return;

	const form = document.createElement('form');
	form.method = 'POST';
	form.action = url;
	form.style.display = 'none';

	appendHiddenInput(form, 'data', data);
	appendHiddenInput(form, 'merchant', merchant);

	document.body.appendChild(form);
	form.submit();
}

/**
 * Form-POST handoff for the /apply and /onboard responses. The gate is the
 * presence of `redirectionUrl` (the contract's `redirectToBank` flag is unused
 * on the client). Returns `true` if a redirect was initiated.
 */
export function handleBankRedirection(
	response: BankRedirectionResponse | OnboardResponse | null | undefined,
): boolean {
	const { merchant = '', data = '', redirectionUrl } = response ?? {};
	if (!redirectionUrl) return false;

	postToBank({ merchant, data, url: redirectionUrl });
	return true;
}

/**
 * Plain GET redirect for the /hf/manage/card response. Returns `true` if a
 * redirect was initiated. No-op during SSR.
 */
export function handleManageCardRedirection(
	response: BankRedirectionResponse | null | undefined,
): boolean {
	const redirectUrl = response?.redirectUrl;
	if (!redirectUrl || typeof window === 'undefined') return false;

	window.location.assign(redirectUrl);
	return true;
}
